import { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Sidebar } from './Sidebar.jsx';
import { Topbar } from './Topbar.jsx';
import { cn } from '@/lib/utils.js';

/**
 * The frame every authenticated screen renders inside.
 *
 * The sidebar is static from `lg` up and a drawer below it, which is what
 * "responsive down to tablet" actually requires: a 60-rem-wide table needs the
 * full viewport on a tablet, and a permanently docked 15-rem sidebar takes a
 * quarter of it.
 */
export function AppShell() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { pathname } = useLocation();

  // Navigating on mobile should close the drawer — otherwise it stays open over
  // the page the operator just asked for.
  useEffect(() => setSidebarOpen(false), [pathname]);

  // Escape closes it, matching every other overlay in the panel.
  useEffect(() => {
    if (!sidebarOpen) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') setSidebarOpen(false);
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [sidebarOpen]);

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      {/*
       * `min-w-0` is load-bearing. Without it a flex child refuses to shrink
       * below its content's intrinsic width, so one wide table would push the
       * whole page into a horizontal scroll instead of scrolling inside its own
       * container.
       */}
      <div className="flex min-w-0 flex-1 flex-col">
        <Topbar onOpenSidebar={() => setSidebarOpen(true)} />

        <main className={cn('flex-1 px-4 py-5 sm:px-6')}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export default AppShell;
